import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import logger from "utils/logger";
import { Link, useHistory } from "react-router-dom";
import { ReactComponent as EyeMasked } from "assets/images/icons/project-icons/eye-masked.svg"
import { ReactComponent as EyeRevealed } from "assets/images/icons/project-icons/eye-revealed.svg"
import useLoginInfo from "hooks/useLoginInfo";
import { clearSignupInfo, createNewUser } from 'store/auth/user/action';
import { ButtonLoader, Input } from 'components/__new/common/form';
import AccountExistsButton from 'components/__new/auth/signup/AccountExistsButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Maybe from 'components/__new/common/Maybe';
import successGif from "assets/images/icons/success-green.gif";


const LOWER_CASE_REGEX = /(?=.*[a-z])/;
const UPPER_CASE_REGEX = /(?=.*[A-Z])/;
const NUMBER_REGEX = /(?=.*[0-9])/;
const SPECIAL_CHARACTER_REGEX = /[^A-Za-z0-9]/;
const EIGHT_CHARACTERS_REGEX = /(?=.{8,})/;


export const CreateBusinessPassword = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const {isLogin, userInfo} = useLoginInfo();
    const [pwdShown, setPwdShown] = useState(false);
    const [successShown, setSuccessShown] = useState(false);
    const [confirmPwdShown, setConfirmPwdShown] = useState(false);
    const [formErrors, setFormErrors] = useState({ password_confirmation: null });
    const [form, setForm] = useState({ password: "", password_confirmation: "" })
    const { newUserDetails, createNewUserLoading: loading, createNewUserData } = useSelector((s) => s.auth.user);
    
    const [lengthValid, setLengthValid] = useState(false);
    const [lowerCaseValid, setLowerCaseValid] = useState(false);
    const [upperCaseValid, setUpperCaseValid] = useState(false);
    const [characterValid, setCharacterValid] = useState(false);
    const [numberValid, setNumberValid] = useState(false);

    const updateForm = (e, fieldName) => {
        const fieldValue = e.target.value
        
        if(fieldValue && fieldValue !== form?.password) {
                setFormErrors({
                    ...formErrors,
                    password_confirmation: "Passwords do not match",
                })
            } else {
                setFormErrors({
                    ...formErrors,
                    password_confirmation: null,
                })
            }
        setForm({
            ...form,
            [fieldName]: fieldValue
        })
    }
    
    const passwordProps = [
        {
            text: "Be at least 8 characters in length",
            isValid: lengthValid,
        },
        {
            text: "Contain at least one lower case character",
            isValid: lowerCaseValid
        },
        {
            text: "Contain at least one upper case character",
            isValid: upperCaseValid
        },
        {
            text: "Contain at least one special character",
            isValid: characterValid
        },
        {
            text: "Contain at least one number",
            isValid: numberValid
        }
    ]
    
    const validatePassword = (password) => {
        setForm({...form, password});
        
        if(EIGHT_CHARACTERS_REGEX.test(password)) {
            setLengthValid(true);
        } else {
            setLengthValid(false);
        }
        
        if(LOWER_CASE_REGEX.test(password)) {
            setLowerCaseValid(true);
        } else {
            setLowerCaseValid(false);
        }
        
        if(UPPER_CASE_REGEX.test(password)) {
            setUpperCaseValid(true);
        } else {
            setUpperCaseValid(false);
        }
        
        if(SPECIAL_CHARACTER_REGEX.test(password)) {
            setCharacterValid(true);
        } else {
            setCharacterValid(false);
        }
        
        if(NUMBER_REGEX.test(password)) {
            setNumberValid(true);
        } else {
            setNumberValid(false);
        }
    }
    
    const submitForm = (e) => {
        e.preventDefault();
        
        const payload = {
                email: newUserDetails.email,
                companyName: newUserDetails.businessName,
                phoneNumber: newUserDetails.phoneNumber,
                password: form.password,
                firstName: newUserDetails.firstName,
                lastName: newUserDetails.lastName,
                // address: "",
                country: newUserDetails.country,
                state: newUserDetails.state,
            }
            dispatch(createNewUser(payload));
    }
    
    const submitDisabled = !lengthValid || !lowerCaseValid || !upperCaseValid || !characterValid || !numberValid || form?.password !== form?.password_confirmation
    
    useEffect(() => {
        if (isLogin === true){
            logger("employer-sign-in", userInfo);
            history.push("/dashboard");
        }
        // eslint-disable-next-line
    }, [isLogin])
    
    useEffect(() => {
        if(createNewUserData?.error === false) {
            setSuccessShown(true);
        }
        // eslint-disable-next-line
    }, [createNewUserData])

    useEffect(() => {
        if(!newUserDetails || newUserDetails === null) history.push("/onboarding/get-started")
        // eslint-disable-next-line
    }, [])

    return (
        <>
            <Maybe condition={!successShown}>
                <div className="w-full text-center pt-8">
                    <div className="text-2xl font-semibold">
                        Create Password
                    </div>
                </div>

                <div className="p-8">
                    <form onSubmit={submitForm}>
                        <div className="w-full max-w-md mx-auto md:mx-0 lg:w-96">
                            <Input
                                label="Password" 
                                autoComplete="off" 
                                placeholder="Enter Password"
                                type={pwdShown ? "text" : "password"} 
                                rightIconClicked={() => setPwdShown(!pwdShown)}
                                rightIcon={<div className='pr-2'>{pwdShown ? <EyeRevealed /> :  <EyeMasked />}</div>}
                                value={form.password} onChange={(e) => validatePassword(e.target.value)} 
                            />
                            
                            <div className="pb-4">
                                {passwordProps.map(({text, isValid}) => (
                                    <div key={text} className="w-full mb-1 flex items-center">
                                        <span>
                                            <FontAwesomeIcon
                                                icon={isValid ? "check-circle" : "times-circle"} 
                                                className={`text-sm transition-all duration-200 ${isValid ? "text-green-500" : "text-red-500"}`}
                                                />
                                        </span>
                                        <p className={`text-sm mb-0 ml-2 transition-colors duration-200 ${isValid ? "text-green-500" : "text-red-500"}`}>
                                            {text}
                                        </p>
                                    </div>
                                ))}
                            </div>
                            
                            <div className="relative">
                                <Input
                                    tagTextIsRed
                                    autoComplete="off" 
                                    label="Confirm Password" 
                                    placeholder="Confirm Password"
                                    value={form.password_confirmation} 
                                    tagText={formErrors?.password_confirmation}
                                    type={confirmPwdShown ? "text" : "password"} 
                                    onChange={(e) => updateForm(e, "password_confirmation")} 
                                />
                                <div className="absolute top-8 right-2">
                                    {confirmPwdShown ? <EyeRevealed onClick={() => setConfirmPwdShown(!confirmPwdShown)} /> :  <EyeMasked onClick={() => setConfirmPwdShown(!confirmPwdShown)} />}
                                </div>
                            </div>
                            
                            <ButtonLoader loading={loading} disabled={submitDisabled || loading} type="submit" className="btn btn-lg btn-block btn-ep-primary mt-5 disabled:!bg-[#E2E8F0] disabled:cursor-not-allowed transition-colors duration-300">
                                {loading ? "Initializing" : "Continue"}
                            </ButtonLoader>
                            
                            <AccountExistsButton />
                        </div>
                    </form>
                </div>
            </Maybe>
            
            <Maybe condition={successShown}>
                <div className="text-center">
                    <div className="table-info max-w-sm">
                        <div>
                            <img src={successGif} alt="Success"  className="mx-auto w-[120px]" />
                        </div>
                        <div className="mt-8 font-semibold text-lg lg:text-xl text-black whitespace-nowrap w-full">
                            Account Created
                        </div>
                        <div className="mt-4 mb-10 text-gray-500">
                            Congratulations! Your account has been created successfully.
                        </div>
                        
                        <div onClick={() => dispatch(clearSignupInfo())}>
                            <Link to="/login" className="btn btn-lg btn-block btn-ep-primary mt-8">Continue</Link>
                        </div>
                    </div>
                </div>
            </Maybe>
            
        </>
    )
}


export default CreateBusinessPassword;